import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { MultiChoice } from '../api/types';
import { api } from '../api/client';
import './MultiChoiceBlock.css';

interface Props {
  sessionName: string;
  data: MultiChoice;
  /** Optional: poll callback to refresh the agent state after sending. */
  onSent?: () => void;
}

/**
 * Renders Claude Code 2.1.x's numbered-choice picker as clickable
 * buttons. Click sends the option number as a single keypress to the
 * agent's tmux pane — Claude Code accepts 1-9 as instant select.
 *
 * Selection model (Alex feedback 2026-06-02): the backend's `opt.selected`
 * (= the `❯` Claude Code rendered in the pane) is intentionally ignored
 * here. The user wants a clean "no option selected" state until THEY
 * click. After click we lock the picker (no double-fires) and show a
 * spinner on the chosen row until the next state-poll either replaces
 * `data` with a new question or removes the picker entirely.
 */
export function MultiChoiceBlock({ sessionName, data, onSent }: Props) {
  const [clickedN, setClickedN] = useState<number | null>(null);

  // Reset when a new question / set of options replaces the current one.
  // Using `data.question` + option count as a fingerprint covers the
  // common case where the backend re-emits the same picker on a brief
  // re-poll race (same identity reference: identity check is fine).
  useEffect(() => {
    setClickedN(null);
  }, [data]);

  const pick = async (n: number) => {
    if (clickedN !== null) return; // already in flight
    setClickedN(n);
    const opt = data.options.find((o) => o.n === n);
    const send = opt?.send ?? { type: 'key' as const, key: String(n) };
    try {
      await api.sessions.answerPrompt(sessionName, send, data.prompt_id);
      if (onSent) onSent();
    } catch (err) {
      console.error('multi-choice send failed', err);
      // Unlock so the user can retry. 409 (Dialog veraltet) landet hier
      // ebenfalls — die Auswahl wurde dann NICHT gesendet.
      setClickedN(null);
    }
  };

  // Esc = dismiss the picker without choosing (Alex 2026-07-16, mobile:
  // "Ich kann nicht esc drücken" — a phone has no Escape key). tmux takes
  // the named key directly; -1 is the lock sentinel for "escape in flight".
  const dismiss = async () => {
    if (clickedN !== null) return;
    setClickedN(-1);
    try {
      await api.sessions.sendKey(sessionName, 'Escape');
      if (onSent) onSent();
    } catch (err) {
      console.error('multi-choice escape failed', err);
      setClickedN(null);
    }
  };

  const locked = clickedN !== null;

  return (
    <div className={`mc-block${locked ? ' is-sending' : ''}`}>
      {data.question && (
        <div className="mc-question">{data.question}</div>
      )}
      <div className="mc-options">
        {data.options.map((opt) => {
          const isChosen = clickedN === opt.n;
          return (
            <button
              key={opt.n}
              className={`mc-option${isChosen ? ' chosen' : ''}`}
              onClick={() => pick(opt.n)}
              disabled={locked}
              title={locked
                ? (isChosen ? 'Sendet …' : 'Andere Option wird gerade gesendet')
                : `Send "${opt.n}" to pick this option`}
            >
              <div className="mc-option-head">
                <span className="mc-option-num">{opt.n}.</span>
                <span className="mc-option-label">{opt.label}</span>
                {isChosen && (
                  <span className="mc-option-spin" aria-label="Sendet">
                    <Loader2 size={12} />
                  </span>
                )}
              </div>
              {opt.description && (
                <div className="mc-option-desc">{opt.description}</div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mc-hint">
        <span className="mc-hint-text">
          {locked
            ? (clickedN === -1 ? 'Sende Esc … warte auf Bot' : `Sende Option ${clickedN} … warte auf Bot`)
            : 'Klick wählt diese Option · Enter im Pane wählt die markierte'}
        </span>
        {!locked && (
          <button
            type="button"
            className="mc-esc"
            onClick={dismiss}
            title="Escape an den Agenten senden — Auswahl abbrechen"
          >
            Abbrechen (Esc)
          </button>
        )}
      </div>
    </div>
  );
}
